import { SubmissionParticipantForReception } from "@dune/types";
import {
	Checkbox,
	H3,
	Table,
	TableBody,
	TableCell,
	TableHeader,
	TableRow,
} from "@dune/ui";
import {
	designerTypeToLabel,
	formatCurrency,
	getParticipantCompanyName,
	traductActivities,
} from "@dune/utils";
import { useInsurerModal } from "../../hooks/useInsurerModal";
import { useShouldDisplayHTAmount } from "../../hooks/useParticipantToSubmissionForm";
import { InsurerSelectionModal } from "../../pages/constructionSites/components/modals/InsurerSelectionModal";
import { ParticipantDocumentsReception } from "../../pages/constructionSites/components/intervenants/ParticipantDocumentsReception";
import {
	BaseSubmissionParticipantTableProps,
	ParticipantInsurerTableCell,
	ParticipantsTableCell,
	ParticipantsTableHead,
} from "./participantsTable.helper";

interface DesignersTableReceptionProps
	extends BaseSubmissionParticipantTableProps<SubmissionParticipantForReception> {
	onDocumentClick?: (file: {
		id: string;
		key: string;
		bucket: string;
		mimeType: string;
		name: string;
	}) => void;
	loadingDocumentId?: string | null;
}

export const DesignersTableReception = ({
	submissionParticipants,
	constructionSiteId,
	selectedParticipantId,
	onParticipantSelect,
	onDocumentClick,
	loadingDocumentId,
	displayInsurer = false,
	disabledDocuments,
	disabledInputs,
}: DesignersTableReceptionProps) => {
	const { shouldDisplayHT } = useShouldDisplayHTAmount(constructionSiteId);
	const { modalState, setModalState, closeModal } = useInsurerModal();

	const totalFees = submissionParticipants.reduce(
		(acc, submissionParticipant) => {
			const participantFees =
				shouldDisplayHT && submissionParticipant.totalFeesHT != null
					? submissionParticipant.totalFeesHT
					: submissionParticipant.totalFeesTTC;
			return acc + participantFees;
		},
		0,
	);

	return (
		<div className="mb-4">
			<H3>CONCEPTEURS</H3>
			<div className="bg-white rounded-lg border overflow-x-hidden">
				<Table className="table-fixed w-full">
					<TableHeader>
						<TableRow>
							<ParticipantsTableHead className="w-64">
								Raison sociale
							</ParticipantsTableHead>
							<ParticipantsTableHead className="w-48">
								Type de concepteur
							</ParticipantsTableHead>
							<ParticipantsTableHead className="w-48">
								Activités réalisées
							</ParticipantsTableHead>
							{displayInsurer && (
								<ParticipantsTableHead className="w-40">
									Assureur
								</ParticipantsTableHead>
							)}

							<ParticipantsTableHead className="w-48">
								Honoraires {shouldDisplayHT ? "(HT)" : "(TTC)"}
							</ParticipantsTableHead>
							<ParticipantsTableHead>
								Pièces justificatives
							</ParticipantsTableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{submissionParticipants.length > 0 ? (
							submissionParticipants.map((submissionParticipant) => {
								const designerTypeLabel = submissionParticipant.designerType
									? designerTypeToLabel[submissionParticipant.designerType]
									: "";
								const activities = traductActivities(
									submissionParticipant.totalMissionsOrActivitiesOrMaterialTypes,
								);
								const fees =
									shouldDisplayHT && submissionParticipant.totalFeesHT != null
										? submissionParticipant.totalFeesHT
										: submissionParticipant.totalFeesTTC;
								const companyName = getParticipantCompanyName(
									submissionParticipant.participant,
								);

								const isSelected =
									selectedParticipantId ===
									submissionParticipant.participant.id;

								return (
									<TableRow key={submissionParticipant.id}>
										<ParticipantsTableCell>
											<div className="flex items-center gap-4">
												{!disabledInputs && (
													<Checkbox
														id={`designer-${submissionParticipant.id}`}
														checked={isSelected}
														onCheckedChange={(checked) => {
															onParticipantSelect(
																submissionParticipant.participant.id,
																checked === true,
															);
														}}
													/>
												)}
												<label
													htmlFor={`designer-${submissionParticipant.id}`}
													className="truncate"
												>
													{companyName}
												</label>
											</div>
										</ParticipantsTableCell>
										<ParticipantsTableCell>
											{designerTypeLabel}
										</ParticipantsTableCell>
										<ParticipantsTableCell
											tooltipContent={
												<div className="flex flex-col gap-1">
													{activities.map((activity) => (
														<span key={activity}>{activity}</span>
													))}
												</div>
											}
										>
											{typeof activities === "string"
												? activities
												: activities.join(", ")}
										</ParticipantsTableCell>
										{displayInsurer && (
											<ParticipantInsurerTableCell
												setModalState={setModalState}
												participantInsurer={submissionParticipant.insurer}
												participantId={submissionParticipant.participant.id}
												constructionSiteId={constructionSiteId}
											/>
										)}
										<ParticipantsTableCell>
											{formatCurrency(fees)}
										</ParticipantsTableCell>
										<ParticipantsTableCell innerClassName="overflow-hidden min-w-0">
											<ParticipantDocumentsReception
												submissionParticipant={submissionParticipant}
												constructionSiteId={constructionSiteId}
												onDocumentClick={onDocumentClick}
												loadingDocumentId={loadingDocumentId}
												disabledDocuments={disabledDocuments}
											/>
										</ParticipantsTableCell>
									</TableRow>
								);
							})
						) : (
							<TableRow>
								<TableCell
									colSpan={displayInsurer ? 7 : 6}
									className="text-center text-gray-500 py-4"
								>
									Aucun concepteur
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
				<div className="flex justify-end px-6 py-4 font-bold text-gray-700 border-t">
					Total concepteurs
					<span className="ml-4 w-40 text-right">
						{formatCurrency(totalFees)}
					</span>
				</div>
			</div>
			<InsurerSelectionModal
				isOpen={modalState.isOpen}
				onClose={closeModal}
				currentInsurer={modalState.currentInsurer}
				participantId={modalState.participantId}
				constructionSiteId={modalState.constructionSiteId}
				onSuccess={closeModal}
			/>
		</div>
	);
};
